import { useEffect, useState } from 'react';
import { institutionApi } from '../../api/institutionsApi';
import { marketsApi } from '../../api/marketsApi';
import { populationRecordsApi } from '../../api/populationRecordsApi';
import { speciesApi } from '../../api/speciesLivestockRegistryApi';
import { livestockActorsApi, workforceDeploymentsApi } from '../../api/workforceApi';

function extractItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
}

function normalizeOption(item, labelResolver, valueResolver) {
  if (item === null || item === undefined) return null;

  if (typeof item === 'string' || typeof item === 'number') {
    const text = String(item).trim();
    return text ? { value: text, label: text } : null;
  }

  const value = String(valueResolver(item) ?? item.uuid ?? item.id ?? '').trim();
  if (!value) return null;

  const label = String(labelResolver(item) ?? value).trim() || value;
  return { value, label, raw: item };
}

function marketLabel(market = {}) {
  const name = market.market_name ?? market.marketName ?? market.name ?? market.uuid ?? market.id ?? '';
  const location = [market.state, market.lga].filter(Boolean).join(' / ');
  return location ? `${name} - ${location}` : name;
}

function deploymentLabel(deployment = {}) {
  const actor = deployment.actorName ?? deployment.fullName ?? deployment.role_category ?? deployment.roleCategory ?? 'Deployment';
  const parts = [actor, deployment.state, deployment.deploymentType ?? deployment.deployment_type]
    .filter(Boolean)
    .join(' - ');
  return parts || actor;
}

function populationLabel(record = {}) {
  const period = record.period ?? record.reference_period ?? record.referencePeriod ?? record.uuid ?? '';
  const parts = [period, record.species, record.productionSystem]
    .filter(Boolean)
    .join(' - ');
  return parts || String(record.uuid ?? record.id ?? 'Population Record');
}

export function useWorkforceReferenceOptions() {
  const [actorOptions, setActorOptions] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [marketOptions, setMarketOptions] = useState([]);
  const [deploymentOptions, setDeploymentOptions] = useState([]);
  const [populationOptions, setPopulationOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const [actorsPayload, institutionsPayload, speciesPayload, marketsPayload, deploymentsPayload] = await Promise.all([
          livestockActorsApi.listAll({ per_page: 200 }),
          institutionApi.list({ per_page: 200 }),
          speciesApi.listAll({ per_page: 200 }),
          marketsApi.list({ per_page: 200 }),
          workforceDeploymentsApi.listAll({ per_page: 200 }),
        ]);
        const populationPayload = await populationRecordsApi.list({ per_page: 200 });

        if (!active) return;

        setActorOptions(
          extractItems(actorsPayload)
            .map((item) => normalizeOption(
              item,
              (row) => row.fullName ?? row.full_name ?? row.actorName ?? row.actor_name,
              (row) => row.uuid ?? row.id
            ))
            .filter(Boolean)
        );

        setInstitutionOptions(
          extractItems(institutionsPayload)
            .map((item) => normalizeOption(
              item,
              (row) => row.name ?? row.institution_name ?? row.institutionName ?? row.label,
              (row) => row.uuid ?? row.id
            ))
            .filter(Boolean)
        );

        setSpeciesOptions(
          extractItems(speciesPayload)
            .map((item) => normalizeOption(
              item,
              (row) => row.common_name_nigeria ?? row.commonNameNigeria ?? row.species_code ?? row.speciesCode ?? row.name,
              (row) => row.uuid ?? row.id
            ))
            .filter(Boolean)
        );

        setMarketOptions(
          extractItems(marketsPayload)
            .map((item) => normalizeOption(item, marketLabel, (row) => row.uuid ?? row.id))
            .filter(Boolean)
        );

        setDeploymentOptions(
          extractItems(deploymentsPayload)
            .filter((item) => !item || !item.role_category || item.role_category === 'extension_worker' || item.roleCategory === 'extension_worker')
            .map((item) => normalizeOption(item, deploymentLabel, (row) => row.uuid ?? row.id))
            .filter(Boolean)
        );

        setPopulationOptions(
          extractItems(populationPayload)
            .map((item) => normalizeOption(item, populationLabel, (row) => row.uuid ?? row.id))
            .filter(Boolean)
        );
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load Workforce reference options.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  return {
    actorOptions,
    institutionOptions,
    speciesOptions,
    marketOptions,
    deploymentOptions,
    populationOptions,
    loading,
    error,
  };
}
